import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Spinner } from "@fluentui/react-components";
import { Switch } from "../ui/CompareToggle/CompareToggle";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";
import useIndicatorsVisibility from "../../hooks/useIndicatorsVisibility";
import { INDICATOR_GROUP_BY_SECTION } from "../../constants";

/**
 * Panneau latéral "Personnaliser l'affichage".
 * Rendu dans PageRoot (pas de portail) pour hériter des variables --prh-*.
 */
const DisplayPrefsPanel = () => {
    const { open, sectionId } = usePilotageRhStore((s) => s.prefPanel);
    const closePrefPanel = usePilotageRhStore((s) => s.closePrefPanel);
    const { groups, setIndicatorVisible, setGroupVisible, showAll } =
        useIndicatorsVisibility();
    const bodyRef = useRef(null);
    const groupRefs = useRef({});
    const [isShowingAll, setIsShowingAll] = useState(false);

    const onShowAll = async () => {
        setIsShowingAll(true);
        try {
            await showAll();
        } finally {
            setIsShowingAll(false);
        }
    };

    /* À l'ouverture depuis "N indicateurs masqués" : défile jusqu'au groupe de la section */
    useEffect(() => {
        if (!open || !bodyRef.current) return;
        const el = groupRefs.current[INDICATOR_GROUP_BY_SECTION[sectionId]];
        bodyRef.current.scrollTop = el ? el.offsetTop - 18 : 0;
    }, [open, sectionId]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => e.key === "Escape" && closePrefPanel();
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, closePrefPanel]);

    const onRowKeyDown = (e, indicator) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            setIndicatorVisible(indicator.code, !indicator.visible);
        }
    };

    return (
        <>
            <Scrim $open={open} onClick={closePrefPanel} />
            <Panel
                $open={open}
                aria-label="Personnaliser l’affichage"
                aria-hidden={!open}
            >
                <Head>
                    <h3>
                        Personnaliser l’affichage
                        <span>
                            Choisissez les indicateurs visibles sur votre
                            tableau de bord
                        </span>
                    </h3>
                    <button
                        type="button"
                        aria-label="Fermer"
                        onClick={closePrefPanel}
                    >
                        ✕
                    </button>
                </Head>

                <Body ref={bodyRef}>
                    {groups.map((group) => {
                        const anyShown = group.indicators.some(
                            (i) => i.visible
                        );
                        return (
                            <div
                                key={group.code}
                                ref={(el) => {
                                    groupRefs.current[group.code] = el;
                                }}
                            >
                                <GroupHead>
                                    <h4>{group.label}</h4>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setGroupVisible(
                                                group.code,
                                                !anyShown
                                            )
                                        }
                                    >
                                        {anyShown
                                            ? "Tout masquer"
                                            : "Tout afficher"}
                                    </button>
                                </GroupHead>
                                {group.indicators.map((indicator) => (
                                    <Row
                                        key={indicator.code}
                                        className={
                                            indicator.visible ? "on" : ""
                                        }
                                        role="switch"
                                        aria-checked={indicator.visible}
                                        tabIndex={0}
                                        onClick={() =>
                                            setIndicatorVisible(
                                                indicator.code,
                                                !indicator.visible
                                            )
                                        }
                                        onKeyDown={(e) =>
                                            onRowKeyDown(e, indicator)
                                        }
                                    >
                                        <Switch />
                                        <span>{indicator.label}</span>
                                    </Row>
                                ))}
                            </div>
                        );
                    })}
                </Body>

                <Foot>
                    <GhostButton
                        type="button"
                        onClick={onShowAll}
                        disabled={isShowingAll}
                        aria-busy={isShowingAll}
                    >
                        {isShowingAll && <Spinner size="extra-tiny" />}
                        Tout réafficher
                    </GhostButton>
                    <PrimaryButton type="button" onClick={closePrefPanel}>
                        Terminé
                    </PrimaryButton>
                </Foot>
            </Panel>
        </>
    );
};

export default DisplayPrefsPanel;

const Scrim = styled.div`
    position: fixed;
    inset: 0;
    z-index: 70;
    background: rgba(0, 0, 0, 0.14);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s;
    ${({ $open }) =>
        $open &&
        css`
            opacity: 1;
            pointer-events: auto;
        `}
`;

const Panel = styled.aside`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 90;
    width: 440px;
    max-width: 94vw;
    display: flex;
    flex-direction: column;
    background: var(--prh-card);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    transform: translateX(105%);
    visibility: hidden;
    transition: transform 0.22s ease, visibility 0s linear 0.22s;
    ${({ $open }) =>
        $open &&
        css`
            transform: translateX(0);
            visibility: visible;
            transition: transform 0.22s ease;
        `}
`;

const Head = styled.div`
    background: var(--prh-violet);
    color: #fff;
    padding: 16px 18px;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    h3 {
        flex: 1;
        margin: 0;
        font-size: var(--prh-font-lg);
        font-weight: 600;
        line-height: 1.3;
    }
    h3 span {
        display: block;
        margin-top: 2px;
        font-size: 11.5px;
        font-weight: 400;
        opacity: 0.85;
    }
    button {
        background: none;
        border: none;
        color: #fff;
        font-size: 18px;
        line-height: 1;
        padding: 2px 6px;
        cursor: pointer;
    }
`;

const Body = styled.div`
    position: relative;
    flex: 1;
    overflow-y: auto;
    padding: 18px;
`;

const GroupHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 20px 0 4px;
    div:first-child > & {
        margin-top: 0;
    }

    h4 {
        margin: 0;
        font-size: var(--prh-font-xs);
        font-weight: 600;
        color: var(--prh-muted-2);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    button {
        background: none;
        border: none;
        padding: 0;
        color: var(--prh-violet);
        font-size: 12px;
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
    }
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 0;
    border-bottom: 1px solid var(--prh-line);
    font-size: var(--prh-font-base);
    color: var(--prh-muted-2);
    cursor: pointer;
    user-select: none;

    &.on {
        color: var(--prh-ink);
    }
    &.on ${Switch} {
        background: var(--prh-violet);
    }
    &.on ${Switch}::after {
        left: 14px;
    }
    &:focus-visible {
        outline: 2px solid var(--prh-violet);
        outline-offset: 2px;
    }
`;

const Foot = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 13px 18px;
    border-top: 1px solid var(--prh-line);
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    padding: 8px 22px;
    border: 1px solid var(--prh-violet);
    border-radius: 6px;
    background: var(--prh-card);
    color: var(--prh-violet);
    font-size: var(--prh-font-sm);
    font-family: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--prh-violet-bg);
    }
    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const PrimaryButton = styled.button`
    white-space: nowrap;
    padding: 8px 28px;
    border: none;
    border-radius: 6px;
    background: var(--prh-violet);
    color: #fff;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;

    &:hover {
        background: var(--prh-violet-dark);
    }
`;
